import { lazy, Suspense, useMemo, type RefObject } from "react";
import type { CurrentResponse, MatchRow } from "@shared/api";
import { ActivityCalendar } from "@features/activity/ActivityCalendar";
import { InsightsFeed } from "@features/analysis/Insights";
import { LifetimeBreakdowns } from "@features/lifetime/LifetimeBreakdowns";
import { aggregateMatches, bucketByDay, rangeRows } from "@shared/lib/matchAggregate";
import { Projections } from "@features/analysis/Projections";
import { Records } from "@features/analysis/Records";
import { KdOverTime } from "@features/charts/KdOverTime";
import { KillsPerMatch } from "@features/charts/KillsPerMatch";
import { WinRateTrend } from "@features/charts/WinRateTrend";
import { Sparkline } from "@shared/ui/Sparkline";
import { useNow } from "@shared/hooks/useNow";
import { WeeklyRecap } from "@features/analysis/WeeklyRecap";
import { RANGE_LABEL } from "@features/dashboard/model/range";
import { SkeletonBlock, SkeletonCard } from "@features/dashboard/ui/shared";
import type { HeroKey, RangeKey, SectionKey } from "@features/dashboard/model/types";

const AnalysisPanel = lazy(() => import("@features/dashboard/ui/AnalysisPanel"));
const ChartsPanel = lazy(() => import("@features/dashboard/ui/ChartsPanel"));
const WeaponsPanel = lazy(() => import("@features/dashboard/ui/WeaponsPanel"));
const MatchesPanel = lazy(() => import("@features/dashboard/ui/MatchesPanel"));

export function DashboardPanels({
  activeSection,
  current,
  allMatches,
  matches,
  range,
  from,
  useDailyBucket,
  refreshKey,
  expandedHero,
  onToggleExpandedHero,
  matchesSectionRef,
  newMatchIds,
}: {
  activeSection: SectionKey;
  current: CurrentResponse | null;
  allMatches: MatchRow[] | null;
  matches: MatchRow[] | null;
  range: RangeKey;
  from: number | undefined;
  useDailyBucket: boolean;
  refreshKey: number;
  expandedHero: HeroKey | null;
  onToggleExpandedHero: (key: HeroKey) => void;
  matchesSectionRef: RefObject<HTMLElement | null>;
  newMatchIds: Set<number>;
}) {
  const mapImages = useMemo(() => {
    const out: Record<string, string> = {};
    for (const map of current?.breakdowns?.maps ?? []) {
      if (map.name && map.image) out[map.name] = map.image;
    }
    return out;
  }, [current]);

  return (
    <main className="tab-panel" id={`section-panel-${activeSection}`} role="tabpanel" aria-labelledby={`section-tab-${activeSection}`}>
      {activeSection === "overview" && (
        <OverviewPanel
          current={current}
          allMatches={allMatches}
          rows={matches}
          range={range}
          from={from}
          useDaily={useDailyBucket}
          refreshKey={refreshKey}
          expanded={expandedHero}
          onToggleExpanded={onToggleExpandedHero}
        />
      )}

      {activeSection === "analysis" && (
        <Suspense fallback={<SkeletonBlock lines={6} />}>
          <AnalysisPanel current={current} allMatches={allMatches} matches={matches} />
        </Suspense>
      )}

      {activeSection === "charts" && (
        <Suspense fallback={<SkeletonBlock lines={8} />}>
          <ChartsPanel matches={matches} range={range} from={from} useDailyBucket={useDailyBucket} refreshKey={refreshKey} />
        </Suspense>
      )}

      {activeSection === "weapons" && (
        <Suspense fallback={<SkeletonBlock lines={6} />}>
          <WeaponsPanel current={current} matches={matches} range={range} />
        </Suspense>
      )}

      {activeSection === "matches" && (
        <Suspense fallback={<SkeletonBlock lines={6} />}>
          <MatchesPanel matches={matches} range={range} matchesSectionRef={matchesSectionRef} newMatchIds={newMatchIds} mapImages={mapImages} />
        </Suspense>
      )}
    </main>
  );
}

function OverviewPanel({
  current,
  allMatches,
  rows,
  range,
  from,
  useDaily,
  refreshKey,
  expanded,
  onToggleExpanded,
}: {
  current: CurrentResponse | null;
  allMatches: MatchRow[] | null;
  rows: MatchRow[] | null;
  range: RangeKey;
  from: number | undefined;
  useDaily: boolean;
  refreshKey: number;
  expanded: HeroKey | null;
  onToggleExpanded: (key: HeroKey) => void;
}) {
  return (
    <>
      <OverviewHero
        current={current}
        rows={rows}
        range={range}
        from={from}
        useDaily={useDaily}
        refreshKey={refreshKey}
        expanded={expanded}
        onToggleExpanded={onToggleExpanded}
      />
      <LatestSession rows={rows} />
      <WeeklyRecap rows={allMatches} />
      <Projections current={current} rows={allMatches} />
      <InsightsFeed current={current} rows={rows} />
      <Records rows={allMatches} bests={current?.bests?.data ?? null} />
      {current && <LifetimeBreakdowns current={current} />}
      <ActivityCalendar refreshKey={refreshKey} />
    </>
  );
}

interface HeroMetric {
  key: HeroKey;
  label: string;
  value: string;
  delta: { text: string; tone: "good" | "bad" | "neutral" } | null;
  spark: number[];
  help: string;
}

function OverviewHero({
  current,
  rows,
  range,
  from,
  useDaily,
  refreshKey,
  expanded,
  onToggleExpanded,
}: {
  current: CurrentResponse | null;
  rows: MatchRow[] | null;
  range: RangeKey;
  from: number | undefined;
  useDaily: boolean;
  refreshKey: number;
  expanded: HeroKey | null;
  onToggleExpanded: (key: HeroKey) => void;
}) {
  const metrics = useHeroMetrics(current, rows, range);

  if (!current) {
    return (
      <div className="hero-grid">
        <SkeletonCard className="hero-card hero-card-primary" />
        <SkeletonCard className="hero-card" />
        <SkeletonCard className="hero-card" />
        <SkeletonCard className="hero-card" />
      </div>
    );
  }

  const hero = metrics[0];
  const rest = metrics.slice(1);
  const { lifetime, derived } = current;
  if (!hero) return null;

  return (
    <>
      <div className="hero-grid">
        <HeroCard metric={hero} primary expandable expanded={expanded === hero.key} onToggle={() => onToggleExpanded(hero.key)} />
        {rest.map((metric) => (
          <HeroCard key={metric.key} metric={metric} expandable expanded={expanded === metric.key} onToggle={() => onToggleExpanded(metric.key)} />
        ))}
      </div>

      {expanded && (
        <div className="hero-chart-panel">
          <div className="chart-title-row">
            <div className="chart-title-text">
              <h2>{expanded === "kd" ? "K/D over time" : expanded === "winRate" ? "Win rate" : expanded === "kpm" ? "Kills per match" : "Kills (total)"}</h2>
              <span className="chart-title-range">· {RANGE_LABEL[range]}</span>
            </div>
            <button className="icon-button icon-button-small" type="button" onClick={() => onToggleExpanded(expanded)} aria-label="Close chart">×</button>
          </div>
          {expanded === "kd" && <KdOverTime from={from} useDaily={useDaily} refreshKey={refreshKey} />}
          {expanded === "winRate" && <WinRateTrend from={from} useDaily={useDaily} refreshKey={refreshKey} />}
          {expanded === "kpm" && <KillsPerMatch from={from} useDaily={useDaily} refreshKey={refreshKey} />}
          {expanded === "kills" && <KillsPerMatch from={from} useDaily={useDaily} refreshKey={refreshKey} />}
        </div>
      )}

      <div className="stat-grid secondary-stat-grid">
        <Card label="Matches" value={lifetime.matches_played.toLocaleString()} sub="lifetime" />
        <Card label="Hours played" value={derived.hours_played.toFixed(1)} sub="lifetime" />
        <Card label="Headshot %" value={`${(derived.headshot_rate * 100).toFixed(1)}%`} sub="lifetime" />
        <Card label="Accuracy" value={`${(derived.accuracy * 100).toFixed(1)}%`} sub="lifetime" />
      </div>
    </>
  );
}

function useHeroMetrics(current: CurrentResponse | null, rows: MatchRow[] | null, range: RangeKey): HeroMetric[] {
  return useMemo(() => {
    if (!current) return [];
    const { derived } = current;
    const days = range === "7d" ? 7 : range === "30d" ? 30 : range === "90d" ? 90 : range === "1y" ? 365 : 180;
    const buckets = rows ? bucketByDay(rows, days) : [];

    const now = Math.floor(Date.now() / 1000);
    const halfSeconds = Math.max(1, Math.floor((days * 86_400) / 2));
    const midpoint = now - halfSeconds;
    const currentRows = rows ? rangeRows(rows, midpoint, now + 1) : [];
    const previousRows = rows ? rangeRows(rows, midpoint - halfSeconds, midpoint) : [];
    const currentAggregate = aggregateMatches(currentRows);
    const previousAggregate = aggregateMatches(previousRows);

    const kdSpark = buckets.map((bucket) => bucket.aggregate.kd ?? 0);
    const killsPerMatchSpark = buckets.map((bucket) => bucket.aggregate.avgKillsPerMatch ?? 0);
    const winRateSpark = buckets.map((bucket) => bucket.aggregate.winRate ?? 0);
    const killsSpark = buckets.map((bucket) => bucket.aggregate.kills);

    return [
      {
        key: "kd",
        label: "K/D",
        value: derived.kd.toFixed(2),
        delta: deltaInline(currentAggregate.kd, previousAggregate.kd, (diff) => diff.toFixed(2), "higher"),
        spark: kdSpark,
        help: "Lifetime K/D from the latest snapshot. Delta and sparkline use tracked match rows over the selected range.",
      },
      {
        key: "winRate",
        label: "Win rate",
        value: `${(derived.win_rate * 100).toFixed(1)}%`,
        delta: deltaInline(currentAggregate.winRate, previousAggregate.winRate, (diff) => `${(diff * 100).toFixed(1)}pt`, "higher"),
        spark: winRateSpark,
        help: "Lifetime win rate from snapshot. Sparkline shows daily in-range win rate; delta compares the latter half of the range vs. the first half.",
      },
      {
        key: "kpm",
        label: "Kills / match",
        value: derived.avg_kills_per_match.toFixed(1),
        delta: deltaInline(currentAggregate.avgKillsPerMatch, previousAggregate.avgKillsPerMatch, (diff) => diff.toFixed(2), "higher"),
        spark: killsPerMatchSpark,
        help: "Kills per match. Lifetime figure from snapshot; delta and sparkline from tracked match rows.",
      },
      {
        key: "kills",
        label: "Kills",
        value: current.lifetime.kills.toLocaleString(),
        delta: deltaInline(currentAggregate.kills, previousAggregate.kills, (diff) => Math.round(diff).toLocaleString(), "higher"),
        spark: killsSpark,
        help: "Lifetime kill count. Sparkline shows per-day kills in the selected range.",
      },
    ];
  }, [current, rows, range]);
}

function HeroCard({
  metric,
  primary,
  expandable,
  expanded,
  onToggle,
}: {
  metric: HeroMetric;
  primary?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const Tag = expandable ? "button" : "div";
  return (
    <Tag
      className={`hero-card ${primary ? "hero-card-primary" : ""} ${expanded ? "is-expanded" : ""}`}
      type={expandable ? "button" : undefined}
      onClick={expandable ? onToggle : undefined}
      aria-expanded={expandable ? expanded : undefined}
    >
      <div className="hero-card-head">
        <div className="hero-card-label">{metric.label}</div>
        {metric.delta && (
          <div className={`hero-card-delta tone-${metric.delta.tone}`}>
            <span aria-hidden="true">{metric.delta.tone === "good" ? "▲" : metric.delta.tone === "bad" ? "▼" : "·"}</span>
            {metric.delta.text}
          </div>
        )}
      </div>
      <div className="hero-card-value">{metric.value}</div>
      <div className="hero-card-spark">
        <Sparkline
          values={metric.spark}
          width={primary ? 260 : 160}
          height={primary ? 44 : 30}
          color={primary ? "var(--accent)" : "var(--hero-line)"}
          fill={primary ? "var(--accent)" : "var(--hero-line)"}
        />
      </div>
      {expandable && <div className="hero-card-foot">{expanded ? "Hide chart" : "View chart"}</div>}
    </Tag>
  );
}

function LatestSession({ rows }: { rows: MatchRow[] | null }) {
  const now = useNow(30_000);
  const session = useMemo(() => {
    if (rows === null || rows.length === 0) return null;
    const sessionRows = extractLatestSession(rows);
    let start = Number.POSITIVE_INFINITY;
    let end = Number.NEGATIVE_INFINITY;
    for (const row of sessionRows) {
      if (row.window_start < start) start = row.window_start;
      if (row.window_end > end) end = row.window_end;
    }
    return {
      rows: sessionRows,
      aggregate: aggregateMatches(sessionRows),
      start,
      end,
    };
  }, [rows]);

  if (rows === null) return <SkeletonCard className="latest-session skeleton-tall" />;
  if (rows.length === 0 || session === null) return null;

  const { aggregate, start, end } = session;

  return (
    <section className="latest-session">
      <div className="latest-session-head">
        <div>
          <div className="latest-session-eyebrow">Last session · ended {formatAgo(end, now)}</div>
          <h2>{sessionWindowLabel(start, end)}</h2>
        </div>
        <div className="latest-session-topline">
          <span className="tag-good">{aggregate.wins} W</span>
          <span className="tag-bad">{aggregate.losses} L</span>
          <span className="tag-muted">{aggregate.observedMatches} matches · {(aggregate.activeSeconds / 60).toFixed(0)} min</span>
        </div>
      </div>
      <div className="latest-session-metrics">
        <MiniStat label="K/D" value={fmtDec(aggregate.kd)} />
        <MiniStat label="Kills" value={aggregate.kills.toLocaleString()} />
        <MiniStat label="Deaths" value={aggregate.deaths.toLocaleString()} />
        <MiniStat label="Kills/Min" value={fmtDec(aggregate.killsPerMinute)} />
        <MiniStat label="Score/Min" value={fmtDec(aggregate.scorePerMinute)} />
        <MiniStat label="Damage" value={aggregate.damage.toLocaleString()} />
        <MiniStat label="Top weapon" value={aggregate.topWeapon?.name ?? "—"} />
        <MiniStat label="Top mode" value={aggregate.topGamemode?.name ?? "—"} />
      </div>
    </section>
  );
}

function MiniStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="mini-stat">
      <div className="mini-stat-label">{label}</div>
      <div className="mini-stat-value">{value}</div>
    </div>
  );
}

function extractLatestSession(rows: MatchRow[]): MatchRow[] {
  const sorted = [...rows].sort((left, right) => right.window_end - left.window_end);
  const session: MatchRow[] = [];
  for (const row of sorted) {
    const previous = session[session.length - 1];
    if (!previous || previous.window_end - row.window_end < 45 * 60) {
      session.push(row);
      continue;
    }
    break;
  }
  return session;
}

function sessionWindowLabel(start: number, end: number): string {
  const startDate = new Date(start * 1000);
  const endDate = new Date(end * 1000);
  const sameDay = startDate.toDateString() === endDate.toDateString();
  const day = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(endDate);
  const time = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
  return sameDay ? `${day}, ${time.format(startDate)} – ${time.format(endDate)}` : `${day}, ${time.format(endDate)}`;
}

function Card({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="stat-card-sub">{sub}</div>}
    </div>
  );
}

function deltaInline(
  currentVal: number | null,
  previousVal: number | null,
  formatDiff: (diff: number) => string,
  direction: "higher" | "lower",
): { text: string; tone: "good" | "bad" | "neutral" } | null {
  if (currentVal === null || previousVal === null) return null;
  const diff = currentVal - previousVal;
  if (!Number.isFinite(diff)) return null;
  const sign = diff > 0 ? "+" : diff < 0 ? "−" : "";
  if (diff === 0) return { text: "flat", tone: "neutral" };
  const better = direction === "higher" ? diff > 0 : diff < 0;
  return { text: `${sign}${formatDiff(Math.abs(diff))}`, tone: better ? "good" : "bad" };
}

function fmtDec(value: number | null): string {
  return value === null ? "—" : value.toFixed(2);
}

function formatAgo(unixSeconds: number, nowMs = Date.now()): string {
  const secondsAgo = Math.max(0, Math.floor(nowMs / 1000) - unixSeconds);
  if (secondsAgo < 45) return "just now";
  const minutesAgo = Math.floor(secondsAgo / 60);
  if (minutesAgo < 60) return `${minutesAgo}m ago`;
  const hoursAgo = Math.floor(minutesAgo / 60);
  if (hoursAgo < 24) return `${hoursAgo}h ago`;
  return `${Math.floor(hoursAgo / 24)}d ago`;
}
